import {
  fetchJobCoordinator,
  fetchJobCoordinatorsByProject,
  fetchMilestoneCoordinator,
  fetchProjectCoordinator,
  fetchProjectAccountManager,
  fetchBillableContext,
  fetchMilestoneContext,
  fetchJobContext,
  fetchCaseStudyContext,
} from "permissions/resolver/api";

export class CoordinatorResolver {
  constructor() {
    this.cache = new Map();
  }

  async resolveCoordinator(type, identifier) {
    // identifier can be a primitive (for backward compatibility) or an object like:
    // { billableId?, milestoneId?, jobId?, projectSlug?, casestudyId? }

    const keyObj =
      typeof identifier === "object" && identifier !== null
        ? identifier
        : { legacy: identifier };

    const cacheKey = `${type}:${JSON.stringify(keyObj)}`;

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    let coordinator = null;

    try {
      switch (type) {
        case "PC":
          if (keyObj.billableId) {
            const billable = await fetchBillableContext(keyObj.billableId);
            coordinator = billable?.job?.project?.coordinator ?? null;
          } else if (keyObj.milestoneId) {
            const milestoneCtx = await fetchMilestoneContext(
              keyObj.milestoneId,
            );
            coordinator = milestoneCtx?.job?.project?.coordinator ?? null;
          } else if (keyObj.jobId) {
            const job = await fetchJobContext(keyObj.jobId);
            coordinator = job?.project?.coordinator ?? null;
          } else if (keyObj.casestudyId) {
            const caseStudy = await fetchCaseStudyContext(keyObj.casestudyId);
            coordinator = caseStudy?.project?.coordinator ?? null;
          }

          if (!coordinator) {
            let projectIdentifier = keyObj.projectSlug ?? keyObj.legacy;

            if (!projectIdentifier && keyObj.casestudyId) {
              const caseStudy = await fetchCaseStudyContext(keyObj.casestudyId);
              projectIdentifier = caseStudy?.project?.project_id;
            }
            if (projectIdentifier) {
              const projectCoordinator =
                await fetchProjectCoordinator(projectIdentifier);
              coordinator = projectCoordinator?.coordinator ?? null;
            }
          }
          break;

        case "SC":
          if (keyObj.billableId) {
            const billable = await fetchBillableContext(keyObj.billableId, [
              "job.project.sales_coordinator",
            ]);
            coordinator = billable?.job?.project?.sales_coordinator ?? null;
          } else if (keyObj.milestoneId) {
            const milestoneCtx = await fetchMilestoneContext(
              keyObj.milestoneId,
              ["job.project.sales_coordinator"],
            );
            coordinator = milestoneCtx?.job?.project?.sales_coordinator ?? null;
          } else if (keyObj.jobId) {
            const job = await fetchJobContext(keyObj.jobId, [
              "project.sales_coordinator",
            ]);
            coordinator = job?.project?.sales_coordinator ?? null;
          } else if (keyObj.casestudyId) {
            const caseStudy = await fetchCaseStudyContext(keyObj.casestudyId);
            coordinator = caseStudy?.project?.sales_coordinator ?? null;
          }

          if (!coordinator) {
            let projectIdentifier = keyObj.projectSlug ?? keyObj.legacy;

            if (!projectIdentifier && keyObj.casestudyId) {
              const caseStudy = await fetchCaseStudyContext(keyObj.casestudyId);
              projectIdentifier = caseStudy?.project?.project_id;
            }
            if (projectIdentifier) {
              const projectSalesCoordinator = await fetchProjectCoordinator(
                projectIdentifier,
                ["sales_coordinator"],
              );
              coordinator = projectSalesCoordinator?.sales_coordinator ?? null;
            }
          }
          break;

        case "AM":
          if (keyObj.billableId) {
            const billable = await fetchBillableContext(keyObj.billableId, [
              "job.project.account_manager",
            ]);
            coordinator = billable?.job?.project?.account_manager ?? null;
          } else if (keyObj.milestoneId) {
            const milestoneCtx = await fetchMilestoneContext(
              keyObj.milestoneId,
              ["job.project.account_manager"],
            );
            coordinator = milestoneCtx?.job?.project?.account_manager ?? null;
          } else if (keyObj.jobId) {
            const job = await fetchJobContext(keyObj.jobId, [
              "project.account_manager",
            ]);
            coordinator = job?.project?.account_manager ?? null;
          } else if (keyObj.casestudyId) {
            const caseStudy = await fetchCaseStudyContext(keyObj.casestudyId);
            coordinator = caseStudy?.project?.account_manager ?? null;
          }

          if (!coordinator) {
            let projectIdentifier = keyObj.projectSlug ?? keyObj.legacy;

            if (!projectIdentifier && keyObj.casestudyId) {
              const caseStudy = await fetchCaseStudyContext(keyObj.casestudyId);
              projectIdentifier = caseStudy?.project?.project_id;
            }
            if (projectIdentifier) {
              const projectAccountManager = await fetchProjectAccountManager(
                projectIdentifier,
                ["account_manager"],
              );
              coordinator = projectAccountManager?.account_manager ?? null;
            }
          }
          break;

        case "JC":
          if (keyObj.billableId) {
            const billable = await fetchBillableContext(keyObj.billableId, [
              "job.coordinator",
            ]);
            coordinator = billable?.job?.coordinator ?? null;
          } else if (keyObj.milestoneId) {
            const milestoneCtx = await fetchMilestoneContext(
              keyObj.milestoneId,
              ["job.coordinator"],
            );
            coordinator = milestoneCtx?.job?.coordinator ?? null;
          } else if (keyObj.jobId) {
            const job = await fetchJobContext(keyObj.jobId, ["coordinator"]);
            coordinator = job?.coordinator ?? null;
          }

          // If we have a projectSlug, fetch all job coordinators for that project
          if (!coordinator && (keyObj.projectSlug || keyObj.casestudyId)) {
            let projectIdentifier = keyObj.projectSlug;

            if (!projectIdentifier && keyObj.casestudyId) {
              const caseStudy = await fetchCaseStudyContext(keyObj.casestudyId);
              projectIdentifier = caseStudy?.project?.project_id;
            }

            const jobs = projectIdentifier
              ? await fetchJobCoordinatorsByProject(projectIdentifier)
              : null;
            if (jobs && jobs.length > 0) {
              // Extract unique coordinators from all jobs
              const coordinators = jobs
                .map((job) => job?.coordinator)
                .filter((coord) => coord !== null && coord !== undefined);

              // Remove duplicates
              const uniqueCoordinators = [...new Set(coordinators)];

              // Return array of coordinators if multiple, single if one, null if none
              coordinator =
                uniqueCoordinators.length > 1
                  ? uniqueCoordinators
                  : (uniqueCoordinators[0] ?? null);
            }
          }

          // Fallback to legacy jobId identifier
          if (!coordinator) {
            const jobIdentifier = keyObj.jobId ?? keyObj.legacy;
            if (jobIdentifier) {
              const job = await fetchJobCoordinator(jobIdentifier);
              coordinator = job?.coordinator ?? null;
            }
          }
          break;

        case "MC":
          // Priority: direct milestoneId -> (no billable path for MC) -> fallback legacy
          const milestoneId = keyObj.milestoneId ?? keyObj.legacy;
          if (milestoneId) {
            const milestone = await fetchMilestoneCoordinator(milestoneId);
            coordinator = milestone?.coordinator ?? null;
          }
          break;

        case "AM":
          if (keyObj.billableId) {
            const billable = await fetchBillableContext(keyObj.billableId, [
              "job.project.account_manager",
            ]);
            coordinator = billable?.job?.project?.account_manager ?? null;
          } else if (keyObj.milestoneId) {
            const milestoneCtx = await fetchMilestoneContext(
              keyObj.milestoneId,
              ["job.project.account_manager"],
            );
            coordinator = milestoneCtx?.job?.project?.account_manager ?? null;
          } else if (keyObj.jobId) {
            const job = await fetchJobContext(keyObj.jobId, [
              "project.account_manager",
            ]);
            coordinator = job?.project?.account_manager ?? null;
          } else if (keyObj.casestudyId) {
            const caseStudy = await fetchCaseStudyContext(keyObj.casestudyId);
            coordinator = caseStudy?.project?.account_manager ?? null;
          }

          if (!coordinator) {
            let projectIdentifier = keyObj.projectSlug ?? keyObj.legacy;

            if (!projectIdentifier && keyObj.casestudyId) {
              const caseStudy = await fetchCaseStudyContext(keyObj.casestudyId);
              projectIdentifier = caseStudy?.project?.project_id;
            }
            if (projectIdentifier) {
              const projectAccountManager = await fetchProjectCoordinator(
                projectIdentifier,
                ["account_manager"],
              );
              coordinator = projectAccountManager?.account_manager ?? null;
            }
          }
          break;

        default:
          throw new Error(`Unknown coordinator type: ${type}`);
      }

      this.cache.set(cacheKey, coordinator);
      return coordinator;
    } catch (error) {
      console.error(
        `Failed to resolve coordinator ${type}:${JSON.stringify(keyObj)}`,
        error,
      );
      return null;
    }
  }

  clearCache() {
    this.cache.clear();
  }
}
